import React from 'react';
import {
  Box,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Typography,
  useMediaQuery,
  useTheme,
  Skeleton
} from '@mui/material';
import { MdOutlineCall, MdApproval, MdLocationOn } from 'react-icons/md';
import { IoChevronDownSharp } from 'react-icons/io5';
import { FaPlus } from 'react-icons/fa6';
import Button from 'components/Common/Button';
import TalkButton from 'components/Common/TalkButton';
import PerformanceIcon from 'assets/icons/PerformanceIcon';
import CallLogsIcon from 'assets/icons/CallLogsIcon';
import ConfigureIcon from 'assets/icons/ConfigureIcon';
import WorksheetIcon from 'assets/icons/Worksheet';
import SignalsIcon from 'assets/icons/SignalsIcon';

const COLLAPSED_WIDTH = 80;
const EXPANDED_WIDTH = 240;

interface Props {
  isMenuOpen?: boolean;
  handleClose?: () => void;
}

export default function SidebarDynamicSkeleton({ isMenuOpen, handleClose }: Props) {
  const theme = useTheme();
  const isMediumScreen = useMediaQuery(theme.breakpoints.down('md'));
  const [isHovered, setIsHovered] = React.useState(true);

  const drawerContent = (
    <Box display='flex' flexDirection='column' height='calc(100vh - 64px)' justifyContent='space-between'>
      <List sx={{ flexGrow: 1, paddingTop: 1, paddingBottom: 8, overflowY: 'auto' }}>
        <Box marginX={2}>
          <Box>
            <List component='nav' aria-label='Agent settings'>
              <ListItemButton
                sx={{
                  background: isHovered ? '' : '#E3E4E5',
                  border: isHovered ? '1px solid #ccc' : '',
                  borderRadius: 2,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: isHovered ? 'flex-start' : 'center',
                  padding: isHovered ? '8px 16px' : '8px 8px',
                  transition: 'padding 0.3s, align-items 0.3s'
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                  <ListItemText
                    primary={
                      <Skeleton
                        variant='text'
                        width={isHovered ? 120 : 30}
                        height={isHovered ? 24 : 20}
                        sx={{
                          textAlign: isHovered ? 'left' : 'center',
                          textTransform: isHovered ? 'capitalize' : 'uppercase'
                        }}
                      />
                    }
                  />
                  {isHovered && <IoChevronDownSharp />}
                </Box>
                {isHovered && (
                  <>
                    <Box color={theme.palette.secondary.main} display='flex' alignItems='start' gap='4px'>
                      <Box height='16px' width='16px'>
                        <MdApproval />
                      </Box>
                      <Skeleton variant='text' width={100} height={16} />
                    </Box>
                    <Box color={theme.palette.secondary.main} display='flex' alignItems='start' gap='4px' marginY={0.2}>
                      <Box height='16px' width='16px'>
                        <MdLocationOn />
                      </Box>
                      <Skeleton variant='text' width={150} height={16} />
                    </Box>
                    <Box color={theme.palette.secondary.main} display='flex' alignItems='start' gap='4px'>
                      <Box height='16px' width='16px'>
                        <MdOutlineCall />
                      </Box>
                      <Skeleton variant='text' width={120} height={16} />
                    </Box>
                  </>
                )}
              </ListItemButton>
            </List>
          </Box>
        </Box>
        {!isHovered && (
          <ListItem>
            <Button color='secondary' variant='outlined' sx={{ borderRadius: 1, minWidth: 'unset' }}>
              <FaPlus />
            </Button>
          </ListItem>
        )}
        {!isHovered && (
          <Typography sx={{ marginTop: '16px', marginBottom: '12px', borderTop: '1px solid #ECECEC', mx: '16px' }} />
        )}
        <ListItem sx={{ justifyContent: 'center' }}>
          <TalkButton isCollapsed={!isHovered} />
        </ListItem>

        {/* Sidebar Items Skeleton */}
        <SidebarItemSkeleton icon={<PerformanceIcon />} isHovered={isHovered} />
        <SidebarItemSkeleton icon={<CallLogsIcon />} isHovered={isHovered} />
        <SidebarItemSkeleton icon={<SignalsIcon />} isHovered={isHovered} />
        <SidebarItemSkeleton icon={<WorksheetIcon />} isHovered={isHovered} />
        <SidebarItemSkeleton icon={<ConfigureIcon />} isHovered={isHovered} />
      </List>

      {/* Bottom Live Mode box Skeleton */}
      <Box sx={{ flexShrink: 0 }}>
        <Box px={2} pb={2}>
          <Box
            sx={{
              backgroundColor: '#F8F9FA',
              border: '1px solid #ECECEC',
              borderRadius: 2,
              padding: 2,
              display: 'flex',
              flexDirection: 'column',
              gap: 1
            }}
          >
            {isHovered ? (
              <>
                <Box display='flex' alignItems='center' gap={1}>
                  <Skeleton variant='circular' width={8} height={8} />
                  <Box display='flex' alignItems='center' justifyContent='space-between' width='100%'>
                    <Skeleton variant='text' width={80} height={20} />
                    <Skeleton variant='rectangular' width={40} height={20} sx={{ borderRadius: 10 }} />
                  </Box>
                </Box>
                <Skeleton variant='text' width={150} height={16} />
              </>
            ) : (
              <Box display='flex' alignItems='center' justifyContent='center' gap={1}>
                <Skeleton variant='circular' width={8} height={8} />
                <Skeleton variant='rectangular' width={40} height={20} sx={{ borderRadius: 10 }} />
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );

  return (
    <Drawer
      variant={isMediumScreen ? 'temporary' : 'permanent'}
      open={isMediumScreen ? isMenuOpen : true}
      onClose={handleClose}
      anchor='left'
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      sx={{
        width: isHovered ? EXPANDED_WIDTH : COLLAPSED_WIDTH,
        flexShrink: 0,
        transition: theme.transitions.create('width', {
          easing: theme.transitions.easing.sharp,
          duration: theme.transitions.duration.standard
        }),
        '& .MuiDrawer-paper': {
          width: isHovered ? EXPANDED_WIDTH : COLLAPSED_WIDTH,
          boxSizing: 'border-box',
          top: isMediumScreen ? 0 : '65px',
          backgroundColor: theme.palette.background.paper,
          transition: theme.transitions.create('width', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.standard
          }),
          overflowX: 'hidden'
        }
      }}
      ModalProps={{
        keepMounted: true
      }}
    >
      {drawerContent}
    </Drawer>
  );
}

// Skeleton component for sidebar items
function SidebarItemSkeleton({ icon, isHovered }: { icon: React.ReactNode; isHovered: boolean }) {
  return (
    <ListItem disablePadding>
      <ListItemButton
        sx={{
          display: 'flex',
          alignItems: 'center',
          padding: isHovered ? '8px 16px' : '8px 8px',
          justifyContent: isHovered ? 'flex-start' : 'center',
          transition: 'padding 0.3s, justify-content 0.3s'
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: isHovered ? 2 : 0 }}>
          {icon}
          {isHovered && <Skeleton variant='text' width={80} height={20} />}
        </Box>
      </ListItemButton>
    </ListItem>
  );
}
